/**
 * Task #4621 — Live, odometer-animated countdown for a custom-quote deadline.
 *
 * The "sent" custom-trip quote card (both the enriched host card and the
 * compact renter card) shows how long the quote has left before it expires.
 * A relative countdown ("Expires in 3 days" → "Expires in 2 hours" →
 * "Expires soon") communicates urgency far more intuitively than a static
 * absolute timestamp, and it ticks down on its own while the conversation
 * stays open.
 *
 * The integer is rendered through the shared `RollingNumber`, so the digit
 * rolls into place like a car odometer (the same delightful motion used for
 * the checkout / booking-widget price total) each time the countdown crosses
 * a boundary. The surrounding words come from i18next plural keys, and we
 * interpolate a sentinel (`{{value}}`) we then split on, so the number lands
 * in the grammatically-correct spot for every locale instead of assuming the
 * English "<verb> <number> <unit>" order.
 *
 * Edge handling mirrors `getQuoteCountdown`:
 *   - missing / unparseable / already-expired → renders nothing (the parent's
 *     status logic owns the terminal "Quote expired" state), so a host never
 *     sees a negative or misleading value.
 *   - under ~2 minutes out → "Expires soon" (no number, nothing to animate).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getQuoteCountdown } from "@/lib/trip-datetime";
import { RollingNumber } from "@/components/ui/rolling-number";

// Re-evaluate twice a minute: frequent enough that the minute figure visibly
// counts down (and the odometer rolls on each change) without churning the
// render tree.
const TICK_MS = 30_000;

// A character that will never appear in translated copy, used to mark where
// the animated number belongs in the localized sentence.
const VALUE_SENTINEL = "\u0000";

export function QuoteExpiryCountdown({
  expiresAt,
  className,
}: {
  expiresAt: string | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const countdown = getQuoteCountdown(expiresAt, now);
  if (!countdown || countdown.kind === "expired") return null;

  if (countdown.kind === "soon") {
    return (
      <span className={className} data-testid="quote-expiry-countdown">
        {t("conversation.quote_expires_soon", "Expires soon")}
      </span>
    );
  }

  const key =
    countdown.kind === "minutes"
      ? "conversation.quote_expires_minutes"
      : countdown.kind === "hours"
        ? "conversation.quote_expires_hours"
        : "conversation.quote_expires_days";

  const fallback =
    countdown.kind === "minutes"
      ? "Expires in {{value}} minutes"
      : countdown.kind === "hours"
        ? "Expires in {{value}} hours"
        : "Expires in {{value}} days";

  // `count` selects the plural form; `value` is the sentinel we split on so the
  // RollingNumber can be slotted in at the number's position in any language.
  const text = t(key, fallback, { count: countdown.count, value: VALUE_SENTINEL });
  const [before, after = ""] = text.split(VALUE_SENTINEL);

  return (
    <span className={className} data-testid="quote-expiry-countdown">
      {before}
      <RollingNumber value={countdown.count} format={(n) => String(Math.round(n))} />
      {after}
    </span>
  );
}
